/* Shopify Inbox / Agent launcher — Zeus brutalist button + tête soleil icon.
   Widget UI lives in #ShopifyChat / shopify-agent shadowRoot; theme CSS cannot
   reach internal nodes. Admin embed only exposes preset icons + colours —
   custom artwork + square hard-shadow CTA requires this script.
   Requires an open (or same-document) shadow root; a closed shadow root blocks
   injection and triggers a console warning after retries. */
(function () {
  "use strict";

  var STYLE_ID = "zeus-inbox-chat-icon";
  var STACK_STYLE_ID = "zeus-inbox-chat-stack";
  var iconUrl = window.themeVariables && window.themeVariables.inboxChatIcon;

  var TOGGLE_SELECTOR =
    ".chat-app > button, button.chat-toggle, .chat-app > .chat-toggle, .chat-toggle, " +
    ".activator, .activator-nudge .activator, button.activator";

  var HOST_SELECTOR =
    "inbox-online-store-chat#ShopifyChat, #ShopifyChat, shopify-chat, " +
    "[id*='ShopifyChat'], [class*='shopify-chat'], shopify-agent, [class*='shopify-agent']";

  function buildNudgeHideCss() {
    /* Shopify Inbox now uses .activator-nudge as the launcher wrapper.
       Hiding that wrapper also hides the live .activator button. */
    return "";
  }

  function buildStackCss() {
    return (
      buildNudgeHideCss() +
      (":host{" +
        "z-index:var(--zeus-z-chat,50)!important;" +
        "}" +
        ".chat-app," +
        ".chat-app > button," +
        "button.chat-toggle," +
        ".chat-toggle," +
        ".activator," +
        ".activator-nudge," +
        ".activator-nudge .activator{" +
        "z-index:var(--zeus-z-chat,50)!important;" +
        "}" +
        /* ZRT-142: Inbox floats the mobile launcher at bottom:80px, i.e. above
           the --zeus-chat-safe-zone band (5.5rem) every hero / page tail reserves,
           so it landed on hero copy and settled card CTAs. Drop it into the band. */
        "@media screen and (max-width:699px){" +
        ".activator-nudge{" +
        "bottom:calc(var(--space-4,1rem) + env(safe-area-inset-bottom,0px))!important;" +
        "}" +
        "}")
    );
  }

  function buildLauncherShapeCss() {
    return (
      buildNudgeHideCss() +
      ("button.chat-toggle," +
        ".chat-app > button," +
        ".chat-app > .chat-toggle," +
        ".activator," +
        ".activator-nudge .activator," +
        "button.activator{" +
        "border-radius:0!important;" +
        "border:2px solid var(--zeus-navy)!important;" +
        "background:var(--zeus-gold)!important;" +
        "color:var(--zeus-navy)!important;" +
        "box-shadow:4px 4px 0 var(--zeus-burnt)!important;" +
        "font-family:var(--ff-display)!important;" +
        "font-weight:900!important;" +
        "letter-spacing:0.06em!important;" +
        "text-transform:uppercase!important;" +
        "min-height:44px!important;" +
        "padding:10px 14px!important;" +
        "display:inline-flex!important;" +
        "align-items:center!important;" +
        "justify-content:center!important;" +
        "gap:8px!important;" +
        "transition:background 0.18s,color 0.18s,transform 0.18s,box-shadow 0.18s!important;" +
        "}" +
        "button.chat-toggle:hover," +
        ".chat-app > button:hover," +
        ".activator:hover," +
        ".activator-nudge .activator:hover," +
        "button.activator:hover{" +
        "background:var(--zeus-sky)!important;" +
        "color:var(--zeus-navy)!important;" +
        "transform:translate(-1px,-1px)!important;" +
        "box-shadow:5px 5px 0 var(--zeus-burnt)!important;" +
        "}")
    );
  }

  function buildCss(url) {
    var escaped = url.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
    return (
      buildLauncherShapeCss() +
      "button.chat-toggle svg," +
      ".chat-app > button svg," +
      ".chat-app > .chat-toggle svg," +
      ".activator svg," +
      ".activator-nudge .activator svg," +
      "button.activator svg," +
      "button.chat-toggle [class*='Icon'] svg," +
      ".chat-app > button [class*='Icon'] svg," +
      ".chat-app > .chat-toggle [class*='Icon'] svg," +
      "button.chat-toggle img," +
      ".chat-app > button img," +
      ".chat-app > .chat-toggle img," +
      ".activator img," +
      "button.activator img{" +
      "opacity:0!important;" +
      "width:0!important;" +
      "height:0!important;" +
      "overflow:hidden!important;" +
      "position:absolute!important;" +
      "pointer-events:none!important;" +
      "}" +
      "button.chat-toggle::before," +
      ".chat-app > button::before," +
      ".chat-app > .chat-toggle::before," +
      ".activator::before," +
      ".activator-nudge .activator::before," +
      "button.activator::before{" +
      "content:'';" +
      "display:inline-block;" +
      "width:1.75rem;" +
      "height:1.75rem;" +
      "margin-inline-end:0.35rem;" +
      "background-image:url('" +
      escaped +
      "');" +
      "background-size:contain;" +
      "background-repeat:no-repeat;" +
      "background-position:center;" +
      "flex-shrink:0;" +
      "vertical-align:middle;" +
      "}" +
      "button.chat-toggle.icon-only::before," +
      "button.chat-toggle.chat-toggle--icon-button::before," +
      "button.chat-toggle.chat-toggle--icon-button.icon-only::before," +
      ".chat-app > .chat-toggle::before," +
      ".activator:not(:has(span))::before," +
      "button.activator.icon-only::before{" +
      "margin-inline-end:0;" +
      "width:2rem;" +
      "height:2rem;" +
      "}"
    );
  }

  function isInboxHost(host) {
    return host && typeof host.matches === "function" && host.matches(HOST_SELECTOR);
  }

  function isInboxRoot(root) {
    return root === document || isInboxHost(root.host) || root.querySelector(TOGGLE_SELECTOR);
  }

  /* Inbox renames/nests its host element across versions, so rather than guess
     a single id we inject into whichever (open) shadow root — or the light DOM —
     actually contains the launcher button. */
  function injectStackIntoRoot(root) {
    if (!root || typeof root.querySelector !== "function") {
      return false;
    }
    if (!isInboxRoot(root)) {
      return false;
    }

    var lookup = typeof root.getElementById === "function" ? root : document;
    if (lookup.getElementById(STACK_STYLE_ID)) {
      return true;
    }

    var style = document.createElement("style");
    style.id = STACK_STYLE_ID;
    style.textContent = buildStackCss();
    (root === document ? document.head : root).appendChild(style);
    return true;
  }

  function injectIntoRoot(root) {
    if (!root || typeof root.querySelector !== "function") {
      return false;
    }
    if (!root.querySelector(TOGGLE_SELECTOR)) {
      return false;
    }

    var lookup = typeof root.getElementById === "function" ? root : document;
    if (lookup.getElementById(STYLE_ID)) {
      return true;
    }

    var style = document.createElement("style");
    style.id = STYLE_ID;
    /* Shape/CTA styles always; icon artwork only when asset URL is available. */
    style.textContent = iconUrl ? buildCss(iconUrl) : buildLauncherShapeCss();
    (root === document ? document.head : root).appendChild(style);
    if (root.host && root.host.setAttribute) {
      root.host.setAttribute("data-zeus-inbox-icon", "true");
    }
    return true;
  }

  function collectShadowRoots(node, acc) {
    var els = node.querySelectorAll ? node.querySelectorAll("*") : [];
    for (var i = 0; i < els.length; i += 1) {
      var sr = els[i].shadowRoot;
      if (sr) {
        acc.push(sr);
        collectShadowRoots(sr, acc);
      }
    }
  }

  function applyChatHosts() {
    var hosts = document.querySelectorAll(HOST_SELECTOR);
    for (var i = 0; i < hosts.length; i += 1) {
      hosts[i].style.setProperty("z-index", "var(--zeus-z-chat, 50)", "important");
    }
  }

  function tryApply() {
    var applied = false;
    injectStackIntoRoot(document);
    applyChatHosts();
    if (injectIntoRoot(document)) {
      applied = true;
    }
    var roots = [];
    collectShadowRoots(document, roots);
    for (var i = 0; i < roots.length; i += 1) {
      injectStackIntoRoot(roots[i]);
      if (injectIntoRoot(roots[i])) {
        applied = true;
      }
    }
    return applied;
  }

  function warnIfClosedShadowRoot() {
    var hosts = document.querySelectorAll(HOST_SELECTOR);
    for (var i = 0; i < hosts.length; i += 1) {
      if (!hosts[i].shadowRoot) {
        console.warn(
          "[zeus-inbox-chat] Could not style chat launcher: host may use a closed shadow root. " +
            "Theme CSS/JS cannot reach closed shadow DOM — verify the Shopify Agent/Inbox embed exposes an open root.",
        );
        return;
      }
    }
  }

  function init() {
    var overlayObserver = new MutationObserver(function () {
      if (window.ZeusOverlayLock) window.ZeusOverlayLock.sync();
      tryApply();
    });
    overlayObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["open", "class"],
      childList: true,
      subtree: true,
    });

    if (tryApply()) {
      return;
    }

    var attempts = 0;
    var observer = null;
    var poll = null;
    var scheduled = false;

    function finish() {
      if (poll) {
        window.clearInterval(poll);
      }
      if (observer) {
        observer.disconnect();
      }
    }

    poll = window.setInterval(function () {
      attempts += 1;
      if (tryApply()) {
        finish();
      } else if (attempts >= 40) {
        warnIfClosedShadowRoot();
        finish();
      }
    }, 500);

    /* Debounced via rAF and torn down on success so we never keep a live
       subtree observer running (that idle observer is itself a jank source). */
    observer = new MutationObserver(function () {
      if (scheduled) {
        return;
      }
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        if (tryApply()) {
          finish();
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
